// frontend\src\utils\addressUtils.ts
// Billing vs. shipping address checks, and the customer email that asks which
// one to ship to.
import { Order } from '../types';

const normalize = (value?: string | null) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// True when the order has a shipping address that differs from billing.
// Compares the address lines only: a company name on the shipping side, or
// "St." vs "St", isn't a different destination.
export function hasAddressConflict(order: Order): boolean {
  if (!order.shipping_address?.trim()) return false;
  const billing = [
    order.customer_address, order.customer_address_2, order.customer_city,
    order.customer_state, order.customer_country, order.customer_zipcode,
  ].map(normalize).join('|');
  const shipping = [
    order.shipping_address, order.shipping_address_2, order.shipping_city,
    order.shipping_state, order.shipping_country, order.shipping_zipcode,
  ].map(normalize).join('|');
  return billing !== shipping;
}

// First line of the confirmation email. Also how the dialog spots that one
// was already sent for this order.
export const ADDRESS_CONFIRMATION_OPENER = 'Thank you for ordering with MPP!';

const formatAddress = (lines: (string | null | undefined)[]) =>
  lines.map((l) => (l || '').trim()).filter(Boolean).join('\n');

// Body of the WooCommerce customer note. WooCommerce turns line breaks into
// paragraphs in the email it sends.
export function buildAddressConfirmationNote(order: Order): string {
  type Part = string | null | undefined;
  const cityLine = (city: Part, state: Part, zip: Part) =>
    [[city, state].filter((v) => v?.trim()).join(', '), zip].filter((v) => v?.trim()).join(' ');

  const billing = formatAddress([
    order.customer_name,
    order.customer_address,
    order.customer_address_2,
    cityLine(order.customer_city, order.customer_state, order.customer_zipcode),
    order.customer_country,
  ]);
  const shipping = formatAddress([
    order.shipping_company,
    order.customer_name,
    order.shipping_address,
    order.shipping_address_2,
    cityLine(order.shipping_city, order.shipping_state, order.shipping_zipcode),
    order.shipping_country,
  ]);

  return [
    ADDRESS_CONFIRMATION_OPENER,
    'Before we ship your order, could you please confirm your shipping address? Your order lists two different addresses, so we like to double-check before it goes out.',
    `Billing address:\n${billing}`,
    `Shipping address:\n${shipping}`,
    "Just reply to this email to let us know which address we should ship to, and we'll get your order on its way.",
  ].join('\n\n');
}
